import React, { useState, useEffect } from "react";
import "./Rules.css";
import * as ApiService from "../services/ApiService";
import { useAuth0 } from "@auth0/auth0-react";

const Rules = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [rules, setRules] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [spacingValue, setSpacingValue] = useState(2); // Default to 2 days
  const [seniorAllowedDays, setSeniorAllowedDays] = useState({
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
    publicHolidays: false,
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const data = await ApiService.fetchSystemRules(getAccessTokenSilently);
      setRules(data);

      // Set initial spacing value from rule parameters
      const spacingRule = data.find(
        (r) => r.rule_key === "no_back_to_back_calls"
      );
      if (spacingRule?.parameters?.spacing_days !== undefined) {
        setSpacingValue(spacingRule.parameters.spacing_days);
      }

      // Set initial allowed days for seniors
      const seniorsRule = data.find(
        (r) => r.rule_key === "seniors_weekdays_only"
      );
      if (seniorsRule?.parameters?.allowedDays) {
        setSeniorAllowedDays(seniorsRule.parameters.allowedDays);
      } else if (seniorsRule?.active) {
        // Default if active but no specific days configured
        setSeniorAllowedDays({
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: false,
          sunday: false,
          publicHolidays: false,
        });
      } else {
        // Default if not active
        setSeniorAllowedDays({
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: true,
          sunday: true,
          publicHolidays: true,
        });
      }
    } catch (error) {
      console.error("Error fetching rules:", error);
      alert("Failed to fetch rules. Please try again.");
    }
  };

  const handleToggle = async (rule) => {
    if (isUpdating) return; // Prevent multiple clicks

    try {
      setIsUpdating(true);
      await ApiService.updateSystemRule(
        getAccessTokenSilently,
        rule.rule_key,
        !rule.active
      );

      // Optimistically update the UI
      setRules((prevRules) =>
        prevRules.map((r) =>
          r.rule_key === rule.rule_key ? { ...r, active: !r.active } : r
        )
      );

      // Refresh rules to ensure consistency
      await fetchRules();
    } catch (error) {
      console.error("Error updating rule:", error);
      alert("Failed to update rule. Please try again.");
      // Revert the optimistic update on error
      await fetchRules();
    } finally {
      setIsUpdating(false);
    }
  };

  const getRuleTitle = (key) => {
    switch (key) {
      case "no_back_to_back_calls":
        return "Duty Spacing";
      case "one_call_per_holiday_block":
        return "One Call per Holiday Block";
      case "seniors_weekdays_only":
        return "Senior Doctor Call Days";
      case "zero_call_correction":
        return "Zero Call Protection";
      default:
        return key;
    }
  };

  const getRuleDescription = (key) => {
    switch (key) {
      case "no_back_to_back_calls":
        return (
          <>
            {spacingValue === 0
              ? "Doctors don't need any days of separation. Back-to-back calls are allowed."
              : `Ensure doctors have `}
            {spacingValue > 0 && (
              <>
                <strong>{spacingValue}</strong>
                {` day${
                  spacingValue === 1 ? "" : "s"
                } of separation between scheduled call duties.`}
              </>
            )}
          </>
        );
      case "one_call_per_holiday_block":
        return (
          <>
            When a public holiday spans multiple days (for example,{" "}
            <em>Christmas</em> or <em>Chinese New Year</em>), a doctor will only
            be scheduled for call duty <strong>once</strong> during that entire
            holiday period.
          </>
        );
      case "seniors_weekdays_only":
        return (
          <>
            Days that are not restricted will never be assigned to{" "}
            <span className="senior">SENIOR</span> doctors.
          </>
        );
      case "zero_call_correction":
        return (
          <>
            Actively seeks opportunities to assign at least one call to every
            eligible doctor, helping to create a more balanced schedule.
          </>
        );
      default:
        return null; // Return null for rules without descriptions
    }
  };

  // Helper function to check if a rule should be displayed
  const shouldDisplayRule = (key) => {
    const supportedRules = [
      "no_back_to_back_calls",
      "one_call_per_holiday_block",
      "seniors_weekdays_only",
      "zero_call_correction",
    ];
    return supportedRules.includes(key);
  };

  const handleSpacingChange = async (value) => {
    if (isUpdating) return;
    setIsUpdating(true);

    try {
      // Update the UI optimistically
      setSpacingValue(value);

      // Update the backend
      await ApiService.updateSystemRule(
        getAccessTokenSilently,
        "no_back_to_back_calls",
        undefined, // Don't change active state
        { spacing_days: value }
      );

      // Refresh rules to ensure consistency
      await fetchRules();
    } catch (error) {
      console.error("Error updating spacing value:", error);
      alert("Failed to update spacing value. Please try again.");
      await fetchRules();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSeniorDaysChange = async (day) => {
    if (isUpdating) return;
    setIsUpdating(true);

    try {
      // Update UI optimistically
      const newAllowedDays = {
        ...seniorAllowedDays,
        [day]: !seniorAllowedDays[day],
      };
      setSeniorAllowedDays(newAllowedDays);

      // Check if at least one day is selected
      const anyDaySelected = Object.values(newAllowedDays).some(
        (value) => value
      );

      // Update the backend
      await ApiService.updateSystemRule(
        getAccessTokenSilently,
        "seniors_weekdays_only",
        anyDaySelected, // Set active based on whether any days are allowed
        { allowedDays: newAllowedDays }
      );

      // Refresh rules to ensure consistency
      await fetchRules();
    } catch (error) {
      console.error("Error updating senior allowed days:", error);
      alert("Failed to update senior allowed days. Please try again.");
      await fetchRules();
    } finally {
      setIsUpdating(false);
    }
  };

  const renderRuleControl = (rule) => {
    if (rule.rule_key === "no_back_to_back_calls") {
      return (
        <div className="rule-status">
          <div className="spacing-slider-container">
            <input
              type="range"
              min="0"
              max="2"
              value={spacingValue}
              onChange={(e) => handleSpacingChange(parseInt(e.target.value))}
              className="spacing-slider"
              data-value={spacingValue}
              style={{ "--thumb-position": `${(spacingValue / 2) * 100}%` }}
            />
            <div className="spacing-labels">
              <span>0</span>
              <span>1</span>
              <span>2</span>
            </div>
          </div>
        </div>
      );
    }

    if (rule.rule_key === "seniors_weekdays_only") {
      return (
        <div className="day-selector-container">
          <div className="day-selector">
            <div
              className={`day-button ${
                !seniorAllowedDays.monday ? "disallowed" : ""
              }`}
              onClick={() => handleSeniorDaysChange("monday")}
            >
              M
            </div>
            <div
              className={`day-button ${
                !seniorAllowedDays.tuesday ? "disallowed" : ""
              }`}
              onClick={() => handleSeniorDaysChange("tuesday")}
            >
              T
            </div>
            <div
              className={`day-button ${
                !seniorAllowedDays.wednesday ? "disallowed" : ""
              }`}
              onClick={() => handleSeniorDaysChange("wednesday")}
            >
              W
            </div>
            <div
              className={`day-button ${
                !seniorAllowedDays.thursday ? "disallowed" : ""
              }`}
              onClick={() => handleSeniorDaysChange("thursday")}
            >
              T
            </div>
            <div
              className={`day-button ${
                !seniorAllowedDays.friday ? "disallowed" : ""
              }`}
              onClick={() => handleSeniorDaysChange("friday")}
            >
              F
            </div>
            <div
              className={`day-button ${
                !seniorAllowedDays.saturday ? "disallowed" : ""
              } weekend`}
              onClick={() => handleSeniorDaysChange("saturday")}
            >
              S
            </div>
            <div
              className={`day-button ${
                !seniorAllowedDays.sunday ? "disallowed" : ""
              } weekend`}
              onClick={() => handleSeniorDaysChange("sunday")}
            >
              S
            </div>
          </div>
          <div
            className={`holiday-button ${
              !seniorAllowedDays.publicHolidays ? "disallowed" : ""
            }`}
            onClick={() => handleSeniorDaysChange("publicHolidays")}
          >
            Public Holidays
          </div>
        </div>
      );
    }

    return (
      <div className="rule-status">
        <span
          className="status-label"
          style={{ color: rule.active ? "#4caf50" : "#f44336" }}
        >
          {rule.active ? "Active" : "Inactive"}
        </span>
        <div
          className={`status-toggle ${rule.active ? "active" : ""}`}
          onClick={() => handleToggle(rule)}
          style={{ backgroundColor: rule.active ? "#c3e6cb" : "#ffcdd2" }}
        />
      </div>
    );
  };

  return (
    <div className="page-container">
      <h2 className="rules-header">Rules Management</h2>
      {rules
        .filter((rule) => shouldDisplayRule(rule.rule_key))
        .map((rule) => (
          <div className="rule-card" key={rule.rule_key}>
            <div className="rule-content">
              <div className="rule-info">
                <h2 className="rule-name">{getRuleTitle(rule.rule_key)}</h2>
                <p className="rule-description">
                  {getRuleDescription(rule.rule_key)}
                </p>
              </div>
              {renderRuleControl(rule)}
            </div>
          </div>
        ))}
    </div>
  );
};

export default Rules;
